/**
 * Dexie-backed client cache for synced entities.
 *
 * Replaces the previous pattern of serialising each entity's full item list
 * into a single localforage blob per account. See .docs/performance.md.
 *
 * Design:
 *
 *   • One table per entity (customers, employees, services, segments, …).
 *   • Rows use a compound primary key `[accountId+id]` so the same browser
 *     can cache data for multiple signed-in accounts without collisions.
 *   • Secondary index on `accountId` so we can cheaply fetch "all items for
 *     the current account" on hydrate and "wipe all items for this account"
 *     when an account is removed from the switcher.
 *   • Row shape is `{ accountId, id, data }` where `data` is the raw plain
 *     object from the API. Model classes are never stored; they're
 *     rehydrated via `new Model(row.data)` on read (same pattern as the
 *     previous localforage code). This keeps schema evolution safe — class
 *     shape changes in app code don't break previously-cached rows.
 *   • Per-entity sync state (last sync date, error flag) lives in its own
 *     `syncStates` table keyed by `${accountId}.${entity}`.
 *
 * Schema versioning:
 *
 *   Only primary keys are declared in v1. Secondary indexes on frequently-
 *   queried fields (e.g. customer email) are deliberately deferred — we'll
 *   add them when we have measurements showing a specific lookup is slow.
 *   Premature indexes bloat the schema and slow every write.
 */
import Dexie, { type Table } from 'dexie'

export interface StoredRow<T = any> {
  accountId: string
  id: string
  data: T
}

export interface StoredSyncState {
  /** `${accountId}.${entity}` — e.g. `abc-123.customers` */
  key: string
  accountId: string
  entity: string
  date: Date | null
  error: boolean
}

class OvatuDB extends Dexie {
  customers!: Table<StoredRow, [string, string]>
  employees!: Table<StoredRow, [string, string]>
  services!: Table<StoredRow, [string, string]>
  segments!: Table<StoredRow, [string, string]>
  products!: Table<StoredRow, [string, string]>
  integrations!: Table<StoredRow, [string, string]>
  forms!: Table<StoredRow, [string, string]>
  voucher!: Table<StoredRow, [string, string]>
  customFields!: Table<StoredRow, [string, string]>
  discountModel!: Table<StoredRow, [string, string]>
  chargeModel!: Table<StoredRow, [string, string]>
  paymentModel!: Table<StoredRow, [string, string]>
  tax!: Table<StoredRow, [string, string]>
  passes!: Table<StoredRow, [string, string]>

  syncStates!: Table<StoredSyncState, string>

  constructor() {
    super('ovatu')

    this.version(1).stores({
      customers: '[accountId+id], accountId',
      employees: '[accountId+id], accountId',
      services: '[accountId+id], accountId',
      segments: '[accountId+id], accountId',
      products: '[accountId+id], accountId',
      integrations: '[accountId+id], accountId',
      forms: '[accountId+id], accountId',
      voucher: '[accountId+id], accountId',
      customFields: '[accountId+id], accountId',
      discountModel: '[accountId+id], accountId',
      chargeModel: '[accountId+id], accountId',
      paymentModel: '[accountId+id], accountId',
      tax: '[accountId+id], accountId',
      passes: '[accountId+id], accountId',
      syncStates: '&key, accountId',
    })
  }
}

/** The keys of per-entity tables — used by the helpers below. */
export type EntityTableName =
  | 'customers'
  | 'employees'
  | 'services'
  | 'segments'
  | 'products'
  | 'integrations'
  | 'forms'
  | 'voucher'
  | 'customFields'
  | 'discountModel'
  | 'chargeModel'
  | 'paymentModel'
  | 'tax'
  | 'passes'

export const db = new OvatuDB()

/**
 * One-time cleanup of the legacy `localforage` IndexedDB database left behind
 * by the old storage layer. Fires as a side-effect of importing this module
 * on the client — runs once per page load. `deleteDatabase` is a no-op when
 * the database doesn't exist (new installs, users who've already been
 * cleaned up, users on other browsers), so this is safe to call
 * unconditionally.
 *
 * The `blocked` event fires if another tab still has the legacy database
 * open — we log it but don't retry, since the next full page reload will
 * try again anyway once connections are closed.
 */
if (typeof indexedDB !== 'undefined') {
  try {
    const req = indexedDB.deleteDatabase('localforage')
    req.onblocked = () => {
      console.warn('[db] Legacy localforage IDB delete blocked — another tab still has it open. Will retry on next page load.')
    }
    req.onerror = () => {
      // Non-fatal: user keeps working against the new Dexie DB; the orphan
      // will just sit idle until the browser evicts it.
      console.warn('[db] Legacy localforage IDB delete failed', req.error)
    }
  } catch {
    // Guard against any runtime without IDB (e.g. test environments).
  }
}

/**
 * Convert a Model instance (or plain object) into a structured-clone-safe
 * payload for persistence. IndexedDB rejects Vue reactive Proxies, plain
 * functions, and any class instance whose prototype isn't a clone-safe
 * built-in (Date/RegExp/Map/Set/etc.) — a single offender anywhere in the
 * object graph aborts the whole transaction with `DataCloneError`.
 *
 * We use `toJSON()` if present (BaseModel provides one that honours the
 * per-field `serialize` hooks declared in each model's `relations()`), and
 * then force a JSON round-trip as a backstop. The round-trip is cheap and
 * lossless for the API-over-the-wire shape we actually persist — if a field
 * isn't JSON-serialisable, it has no business surviving a server round-trip
 * either, so we'd rather drop it than corrupt the store.
 */
export function toStorableData(item: any): any {
  const source = (item && typeof item.toJSON === 'function') ? item.toJSON() : item
  return JSON.parse(JSON.stringify(source))
}

/**
 * Wipe every row belonging to an account across all per-entity tables and
 * its sync-state rows. Used by the auth plugin when the user removes an
 * account from the switcher. Runs inside a single transaction so partial
 * failures don't leave orphaned fragments.
 */
export async function deleteAccountData(accountId: string): Promise<void> {
  const entityTables: EntityTableName[] = [
    'customers',
    'employees',
    'services',
    'segments',
    'products',
    'integrations',
    'forms',
    'voucher',
    'customFields',
    'discountModel',
    'chargeModel',
    'paymentModel',
    'tax',
    'passes',
  ]

  await db.transaction(
    'rw',
    [...entityTables.map(name => db.table(name)), db.syncStates],
    async () => {
      for (const name of entityTables) {
        await db.table(name).where('accountId').equals(accountId).delete()
      }
      await db.syncStates.where('accountId').equals(accountId).delete()
    }
  )
}
